import 'twin.macro';
import React, { useRef, useState } from 'react';
import {
  LivretEleve,
  LivretEval,
} from '@teetsh/app/src/containers/Livrets/types/livret';
import { useUpdateLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEleve';
import { LSUCompatibility } from '@teetsh/app/src/containers/Livrets/LivretEval/LSUCompatibility';
import BrandedSelect from '@teetsh/app/src/components/Form/Select2/Branded';
import { useDownloadLSU } from '@teetsh/app/src/containers/Livrets/queries/useDownloadLSU';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ArrowDownTrayIcon as Download,
  ArrowLeftIcon as ArrowLeft,
  Cog8ToothIcon as Cog,
  QuestionMarkCircleIcon as QuestionMarkCircle,
  TrashIcon as Trash,
  EllipsisVerticalIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useDownloadLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/useDownloadLivretEleve';
import { ArrowDownCircleIcon as ArrowCircleDown } from '@heroicons/react/20/solid';
import SchoolyearButtonLink from '@teetsh/app/src/components/Buttons/SchoolyearButtonLink';
import livretsUrls from '@teetsh/app/src/containers/Livrets/urls';
import { Menu } from '@teetsh/app/src/components/Menu';
import { DeleteLivretEleveWarningModal } from '@teetsh/app/src/containers/Livrets/LivretEval/LivretEleve/DeleteLivretEleveWarningModal';
import { DownloadLSUModal } from '@teetsh/app/src/containers/Livrets/LivretEval/components/DownloadLSUModal';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';
import ButtonExternal from '@teetsh/app/src/components/Buttons/ExternalLink';
import { Eleve } from 'types/eleves';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { tutoUrls } from 'containers/tutoUrls';
import { useTranslation } from 'react-i18next';
import { SyncLivretModal } from './components/LivretEleve/SyncLivret/SyncLivretModal';
import { trackEvent } from 'queries/user/track';

type DownloadModalState =
  | { type: 'closed' }
  | { type: 'multipleSelection' }
  | { type: 'singleSelection'; eleveId: string };

interface Props {
  livretEval: LivretEval;
  livretEleve: LivretEleve;
  eleve: Eleve;
  backLink?: string;
  readonly?: boolean;
}

export function LivretEleveHeader(props: Props) {
  const { livretEleve, livretEval, eleve, readonly } = props;
  const { isNotLoggedIn } = useUser();
  const { t } = useTranslation(['carnet', 'common']);
  const goTo = useSchoolGoTo();

  const livretEvalId = livretEval.id;
  const livretEvalName = livretEval.name;
  const eleveName = `${eleve.familyName} ${eleve.givenName}`;

  const [downloadModalState, setDownloadModalState] =
    useState<DownloadModalState>({ type: 'closed' });
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const headerRef = useRef<HTMLDivElement>(null);

  const updateLivretEleve = useUpdateLivretEleve({
    livretEleveId: livretEleve.id,
    livretEvalId,
  });

  const {
    isDownloading: isDownloadingLivretEleve,
    download: downloadLivretEleve,
  } = useDownloadLivretEleve();

  const { isDownloading: isDownloadingLSU } = useDownloadLSU({ livretEvalId });

  const isDownloadModalOpen = downloadModalState.type !== 'closed';
  const downloadModalEleveId =
    downloadModalState.type === 'singleSelection'
      ? downloadModalState.eleveId
      : undefined;

  return (
    <div tw="relative">
      <div ref={headerRef} tw="absolute inset-x-0 -my-6" />

      <DeleteLivretEleveWarningModal
        eleveName={eleveName}
        livretEleveId={livretEleve.id}
        livretEvalId={livretEvalId}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      <SyncLivretModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        notationSystem={livretEval.notationSystem}
        livretEvalId={livretEval.id}
        livretEleve={livretEleve}
      />

      <DownloadLSUModal
        livretEleveId={downloadModalEleveId}
        livretEvalId={livretEvalId}
        isOpen={isDownloadModalOpen}
        onClose={() => setDownloadModalState({ type: 'closed' })}
      />

      <div tw="flex xl:hidden justify-between items-center mb-2">
        <SchoolyearButtonLink
          withSchoolContext={!props.backLink}
          minimal
          icon={ArrowLeft}
          to={props.backLink || `${livretsUrls.livretDetails(livretEvalId)}`}
        >
          {t('common:buttons.back')}
        </SchoolyearButtonLink>
      </div>
      <div tw="flex justify-between items-center mb-4">
        <div />
        <div tw="flex flex-wrap items-center gap-4">
          {!readonly && (
            <BrandedSelect
              options={[
                {
                  value: 'draft',
                  label: t('carnet:livret.draft'),
                  color: 'yellow-500',
                },
                {
                  value: 'done',
                  label: t('carnet:livret.done'),
                  color: 'green-500',
                },
              ]}
              value={livretEleve.status}
              onChange={status =>
                updateLivretEleve({
                  ...livretEleve,
                  status,
                })
              }
            />
          )}

          <Button
            loading={isDownloadingLivretEleve}
            onClick={() =>
              downloadLivretEleve({
                livretEleveId: livretEleve.id,
                livretName: livretEvalName,
                eleveName,
              })
            }
            large
            icon={ArrowCircleDown}
          >
            {t('common:buttons.downloadAsPdf')}
          </Button>

          {!isNotLoggedIn && (
            <Menu large portal>
              <Menu.Button
                large
                icon={EllipsisVerticalIcon}
                loading={isDownloadingLSU}
              />
              <Menu.Items size="auto">
                <Menu.Item
                  loading={isDownloadingLSU}
                  text={t('carnet:livret.exportLSUMultipleEleves')}
                  onClick={() =>
                    setDownloadModalState({ type: 'multipleSelection' })
                  }
                  icon={Download}
                />
                <Menu.Item
                  disabled={livretEleve.lsuErrors.length > 0}
                  loading={isDownloadingLSU}
                  text={t('carnet:livret.exportLSUSingleEleve')}
                  onClick={() =>
                    setDownloadModalState({
                      type: 'singleSelection',
                      eleveId: livretEleve.id,
                    })
                  }
                  icon={Download}
                />
                <Menu.Item
                  disabled={livretEleve.status !== 'draft'}
                  text={t('carnet:syncLivret.checkGrades')}
                  onClick={() => {
                    trackEvent('sync-livret : open modal');
                    setIsSyncModalOpen(true);
                  }}
                  icon={ArrowPathIcon}
                />
                <Menu.Item
                  loading={isDownloadingLSU}
                  text={t('carnet:livret.configureLivret')}
                  onClick={() => goTo(livretsUrls.livretConfig(livretEvalId))}
                  icon={Cog}
                />
                <Menu.Item>
                  <ButtonExternal
                    tw="whitespace-nowrap rounded-none"
                    href={tutoUrls.presentationLsu}
                    large
                    minimal
                    fill
                    alignText="left"
                    icon={QuestionMarkCircle}
                  >
                    {t('common:buttons.openTuto')}
                  </ButtonExternal>
                </Menu.Item>
                <Menu.Item
                  onClick={() => setIsDeleteModalOpen(true)}
                  text={t('carnet:livret.deleteLivret')}
                  intent="danger"
                  tw="whitespace-nowrap"
                  icon={Trash}
                />
              </Menu.Items>
            </Menu>
          )}
        </div>
      </div>

      {!isNotLoggedIn && (
        <div tw="flex justify-between items-start mb-4">
          <LSUCompatibility
            errors={livretEleve.lsuErrors}
            livretEvalId={livretEvalId}
            eleveId={eleve.id}
          />
        </div>
      )}
    </div>
  );
}
